import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowLeft, Eye, Lock, Pen, Plus, Search, ShieldCheck, Trash2, Users } from 'lucide-react';
import { useSharedStore } from '../../hooks/useSharedStore';
import { PermissionChecklist } from '../../components/PermissionChecklist';
import { ConfirmModal } from '../../components/ConfirmModal';
import { IconAction, RowActions } from '../../components/RowActions';
import { ViewToggle } from '../../components/ViewToggle';
import { orgUsersOf } from '../../data/store';
import {
  roles, accessFeatures, groupedFeatures,
  loadRoles, loadAccessFeatures, saveRole, deleteRole, offeredRoles,
} from '../../data/accessStore';

/**
 * Roles & Access — where a shop decides what its staff can do.
 *
 * The page is a header bar and a grid of cards, and nothing between them. The
 * bar carries the title, two counted figures, and the three controls; the cards
 * carry everything else, each one saying whether it is built in and how many
 * people hold it.
 *
 * It did once open with a summary panel and band the cards under headings —
 * "can do anything", "nobody is using", "everyday roles". On a shop with three
 * roles that was more heading than list, and every fact in it is already
 * printed on the card it belongs to. A screen this short does not need a
 * summary of itself.
 *
 * Only the roles this shop can actually use are listed. Every organisation is
 * seeded with every built-in role whatever it pays for, so one with no
 * warehouse was being offered Warehouse Admin — a role whose screens it does
 * not have, presented as though it were a choice.
 *
 * There is no second sidebar. This screen already sits beside the app's own
 * navigation, and a list rail inside it put two vertical columns of links on
 * one page, which is one more than any page needs.
 *
 * Permissions are not on this screen. Three dozen screens times four boxes is
 * a wall, and a wall shown to somebody who came to check who is an admin is
 * noise. It appears when a role is opened, and not before.
 *
 * The checklist itself is fetched, not written out here. The server decides
 * which features this shop has, from its type and the modules it bought, and
 * both the middleware and this page read that same list — so the boxes on
 * screen are exactly the permissions that will be enforced.
 */

const ACTION_KEYS = ['view', 'create', 'edit', 'delete'];

const permissionsOf = (role) => {
  const out = {};
  (role.permissions || []).forEach((p) => {
    out[p.feature] = { view: !!p.view, create: !!p.create, edit: !!p.edit, delete: !!p.delete };
  });
  return out;
};

// Counts only the boxes this shop's checklist actually offers.
//
// A role keeps permission rows for the whole catalogue, including features the
// shop does not have — a module it never bought, or one switched off. The
// features endpoint returns only what this shop has. Counting the stored rows
// against that shorter list produced "103 of 81": a hundred and three flags on
// file, eighty-one boxes on screen.
//
// So the count walks the catalogue rather than the stored rows, and asks each
// feature only about the actions it supports. Granted can then never exceed
// available, because both are measured off the same list.
//
// The stored rows are left alone. Filtering them here would look tidier and
// would quietly drop a module's permissions the next time the role was saved,
// so switching that module back on would return it stripped of access it used
// to have.
const countGranted = (perms, features) =>
  features.reduce(
    (n, f) => n + (f.actions || []).filter((a) => perms[f.key]?.[a]).length,
    0,
  );

// Every box the catalogue offers, ticked. A role marked `isLocked` is the
// seeder's "grants everything", and it carries no permission rows to say so —
// the server treats the flag itself as the grant. Reading it literally left the
// checklist blank for Organization Admin, which answered "what can an admin do?"
// with "nothing".
const everything = () => {
  const out = {};
  accessFeatures.forEach((f) => {
    out[f.key] = {};
    (f.actions || []).forEach((a) => { out[f.key][a] = true; });
  });
  return out;
};

// `locked` rides along on the draft so the editor knows it may only look. It
// is read from the role, never from the form, so nothing the user types can
// turn a built-in role into an editable one.
const draftOf = (role) => ({
  id: role.id,
  name: role.name,
  description: role.description || '',
  permissions: role.isLocked ? everything() : permissionsOf(role),
  locked: !!role.isLocked,
});

const sameDraft = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export const RolesAclTab = ({ org, showNotification }) => {
  const { version } = useSharedStore();
  const [editing, setEditing] = useState(null);
  const [pristine, setPristine] = useState(null);
  const [saving, setSaving] = useState(false);
  const [nameMissed, setNameMissed] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [search, setSearch] = useState('');
  // Same two-button switch, same default, as Products, Services and the master
  // tabs. A screen that invents its own way of doing this makes the app feel
  // like several apps.
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    loadRoles();
    loadAccessFeatures();
  }, []);

  const groups = useMemo(() => groupedFeatures(), [accessFeatures.length, version]);

  // Every box the checklist actually offers. A permission count only means
  // something next to what was available to grant — "11" says nothing, "11 of
  // 118" says this role is narrow, and the two are comparable card to card.
  const grantable = useMemo(
    () => accessFeatures.reduce((n, f) => n + (f.actions?.length || 0), 0),
    [accessFeatures.length, version],
  );

  // Who holds each role — the question that decides whether deleting is safe,
  // and the one an administrator asks before changing anything.
  const holders = useMemo(() => {
    const map = {};
    orgUsersOf(org.id).forEach((u) => {
      if (u.roleId) map[u.roleId] = (map[u.roleId] || 0) + 1;
    });
    return map;
  }, [org.id, version]);

  // The roles this shop is actually offered. The rule lives in accessStore
  // because the user list asks the same question to fill its Role dropdown, and
  // the two screens have to give the same answer.
  const available = useMemo(
    () => offeredRoles(org, new Set(Object.keys(holders))),
    [roles.length, version, org, holders],
  );

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return available;
    return available.filter((r) =>
      (r.name || '').toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q));
  }, [search, available]);

  // What the header says under the title.
  //
  // Counted from `available`, not from every seeded row: a shop shown one role
  // must not be told it has five. Two figures only — how many roles this shop
  // has, and how many people are actually on one — because those are the two a
  // person can act on. Everything finer belongs on the card that owns it.
  const tally = useMemo(() => {
    const assigned = available.reduce((n, r) => n + (holders[r.id] || 0), 0);
    return { roles: available.length, assigned };
  }, [available, holders]);

  // One group, unlabelled.
  //
  // The roles used to be banded — "Can do anything", "Nobody is using",
  // "Everyday roles" — each with a heading and a line of explanation. On a shop
  // with two or three roles that is more heading than list, and the cards say
  // what they are anyway: the locked one is marked BUILT IN, and each card
  // prints how many people hold it. The grouping was answering a question
  // nobody had yet asked.
  const bands = useMemo(
    () => (shown.length ? [{ key: 'all', items: shown }] : []),
    [shown],
  );

  const open = (draft) => {
    setNameMissed(false);
    setEditing(draft);
    setPristine(draft);
  };
  const close = () => { setEditing(null); setPristine(null); };

  const startNew = () => open({ name: '', description: '', permissions: {}, locked: false });
  const startEdit = (role) => open(draftOf(role));

  const save = async () => {
    if (!editing.name.trim()) { setNameMissed(true); return; }
    setSaving(true);
    const res = await saveRole(editing);
    setSaving(false);
    if (!res.ok) { showNotification?.(res.error, 'error'); return; }
    showNotification?.(editing.id ? 'Role updated.' : 'Role created.', 'success');
    close();
  };

  const doConfirm = async () => {
    const c = confirming;
    setConfirming(null);
    const res = await deleteRole(c.role.id);
    if (!res.ok) showNotification?.(res.error, 'error');
    else {
      showNotification?.(`“${c.role.name}” deleted.`, 'success');
      if (editing?.id === c.role.id) close();
    }
  };

  // What a role is, measured once per role rather than three times in markup.
  //
  // `areas` is the list, not the count. "6 of 13" is arithmetic a shop owner
  // then has to translate; "Billing, Customers, Inventory" is the answer they
  // were translating it into. The number stays for the table, where columns of
  // figures are the point and names would not line up.
  const measure = (role) => {
    const perms = permissionsOf(role);
    const areas = groups
      .filter((g) => g.items.some((f) => ACTION_KEYS.some((a) => perms[f.key]?.[a])))
      .map((g) => g.group);
    return {
      granted: countGranted(perms, accessFeatures),
      areas,
      staff: holders[role.id] || 0,
    };
  };

  const card = 'bg-white border border-slate-200 rounded-md shadow-[0_1px_2px_rgba(15,23,42,0.05)]';

  // ── The editor ──────────────────────────────────────────────────────────
  if (editing) {
    const granted = countGranted(editing.permissions, accessFeatures);
    const areasUsed = groups.filter(
      (g) => g.items.some((f) => ACTION_KEYS.some((a) => editing.permissions[f.key]?.[a])),
    ).length;
    const dirty = pristine && !sameDraft(editing, pristine);
    // A new role saves as soon as it has a name. Requiring a change from an
    // empty form meant the button sat greyed out on a screen whose whole
    // purpose was to create something, which reads as broken.
    const canSave = !editing.locked && (editing.id ? dirty : !!editing.name.trim());

    return (
      <article className="flex flex-col gap-3 pb-6">
        <header className="flex items-center gap-2 flex-wrap px-0.5">
          <button
            type="button"
            onClick={close}
            className="h-8 pl-1.5 pr-2.5 rounded-md flex items-center gap-1 text-body font-medium text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
          >
            <ArrowLeft size={15} aria-hidden="true" /> All roles
          </button>
          {editing.locked && (
            <span className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full bg-amber-50 text-amber-800 text-mini font-semibold">
              <Lock size={11} aria-hidden="true" /> Built in · view only
            </span>
          )}
          <span className="ml-auto flex items-center gap-2">
            {editing.id && !editing.locked && !(holders[editing.id] > 0) && (
              <button
                type="button"
                onClick={() => setConfirming({ role: roles.find((r) => r.id === editing.id) })}
                className="h-8 px-3 rounded-md border border-slate-200 bg-white text-body font-medium text-slate-600 hover:text-rose-600 hover:border-rose-200 hover:bg-rose-50 transition-colors"
              >
                Delete
              </button>
            )}
            {!editing.locked && (
              <button
                type="button"
                onClick={save}
                disabled={saving || !canSave}
                className="h-8 px-4 rounded-md text-white text-body font-semibold shadow-sm transition-opacity hover:opacity-90 disabled:opacity-40"
                style={{ background: 'var(--theme-primary)' }}
              >
                {saving ? 'Saving…' : editing.id ? 'Save changes' : 'Create role'}
              </button>
            )}
          </span>
        </header>

        <section className={`${card} overflow-hidden`}>
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex flex-col">
              <span className="text-body font-semibold text-slate-700 mb-1.5">Role name</span>
              <input
                value={editing.name}
                onChange={(e) => { setNameMissed(false); setEditing({ ...editing, name: e.target.value }); }}
                placeholder="e.g. Cashier"
                disabled={editing.locked}
                className={`w-full h-9 bg-white border rounded-md px-3 text-read font-medium text-[#0d0d0d] placeholder:font-normal placeholder:text-slate-400 outline-none transition-colors focus:ring-3 focus:ring-[var(--theme-primary-soft)] disabled:bg-slate-50 disabled:text-slate-500 ${
                  nameMissed && !editing.name.trim()
                    ? 'border-amber-400 focus:border-amber-500'
                    : 'border-slate-200 focus:border-[var(--theme-primary)]'
                }`}
              />
              {nameMissed && !editing.name.trim() && (
                <span className="text-mini font-medium text-amber-700 mt-1.5">
                  Give it a name your staff will recognise.
                </span>
              )}
            </label>
            <label className="flex flex-col">
              <span className="text-body font-semibold text-slate-700 mb-1.5">What this role is for</span>
              <input
                value={editing.description}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                placeholder="e.g. Bills customers, cannot change prices"
                disabled={editing.locked}
                className="w-full h-9 bg-white border border-slate-200 rounded-md px-3 text-read text-[#0d0d0d] placeholder:text-slate-400 outline-none transition-colors focus:border-[var(--theme-primary)] focus:ring-3 focus:ring-[var(--theme-primary-soft)] disabled:bg-slate-50 disabled:text-slate-500"
              />
            </label>
          </div>

          {/* Granted against available, in one line, so the width of this role
              needs no second look. */}
          <div className="flex items-center gap-3 flex-wrap px-4 py-2.5 border-t border-slate-100 bg-slate-50/70">
            {editing.id && (
              <span className="inline-flex items-center gap-1.5 h-[22px] px-2 rounded-full border border-slate-200 bg-white text-mini font-medium text-slate-600 tabular-nums">
                <Users size={11} className="text-slate-400" aria-hidden="true" />
                {holders[editing.id] || 0} staff
              </span>
            )}
            <span className="text-body text-slate-600">
              <b className="text-head font-semibold tabular-nums text-[#0d0d0d]">
                {editing.locked ? grantable : granted}
              </b>
              <span className="text-slate-400"> / {grantable || '—'}</span> permissions
            </span>
            <span className="text-body text-slate-600">
              <b className="text-head font-semibold tabular-nums text-[#0d0d0d]">
                {editing.locked ? groups.length : areasUsed}
              </b>
              <span className="text-slate-400"> / {groups.length}</span> areas
            </span>
          </div>
        </section>

        <section className={`${card} p-4`}>
          <h3 className="text-read font-semibold text-[#0d0d0d]">What this role can do</h3>
          <p className="text-body text-slate-500 mt-0.5 mb-3 max-w-[68ch]">
            {editing.locked
              ? 'This role is built in and grants everything. It cannot be changed.'
              : 'Only the screens your shop has are listed. Ticking Add, Edit or Delete turns View on too — nobody can change a screen they cannot open.'}
          </p>
          <PermissionChecklist
            key={editing.id || 'new'}
            groups={groups}
            value={editing.permissions}
            onChange={(permissions) => setEditing({ ...editing, permissions })}
            readOnly={editing.locked}
          />
        </section>

        {confirming && (
          <ConfirmModal
            open
            title={`Delete “${confirming.role?.name}”?`}
            message="Nobody is using this role, so nothing else changes."
            confirmText="Delete"
            onCancel={() => setConfirming(null)}
            onConfirm={doConfirm}
          />
        )}
      </article>
    );
  }

  // ── The roles ───────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-3 pb-6">
      {/* The controls belong to something.

          Search, the view switch and Add role used to float loose on the page
          background, and once the summary panel above them was taken out they
          were the first thing on the screen: three unrelated controls hanging
          in grey space with nothing to say what page they were on. A toolbar
          without a surface does not read as a toolbar, it reads as a gap.

          So they are seated in a bar built like the role cards underneath —
          same white, same border, same radius — which gives the page one
          shape instead of two, and gives the title somewhere to live.

          The caption under the title is two figures and no more. The panel
          that stood here before was a 38px number, a sentence, a subtitle and
          a four-column table of counts, all above a list that on most shops is
          shorter than the summary describing it. These two are the ones an
          owner acts on, and they are set as a caption because that is what
          they are. */}
      <header className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-3">
        <div className="flex items-center gap-3 mr-auto min-w-0">
          <span
            className="hidden sm:flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
            style={{ background: 'var(--theme-primary-soft)', color: 'var(--theme-primary)' }}
            aria-hidden="true"
          >
            <ShieldCheck size={17} strokeWidth={2.2} />
          </span>
          <div className="min-w-0">
            <h2 className="text-body font-extrabold uppercase tracking-wide text-[#0d0d0d] leading-none">
              Roles &amp; Access
            </h2>
            <p className="mt-1.5 text-micro font-semibold text-slate-500 leading-none">
              <span className="tabular-nums font-bold text-[#0d0d0d]">{tally.roles}</span>
              {tally.roles === 1 ? ' role' : ' roles'}
              <span className="mx-1.5 text-slate-300" aria-hidden="true">·</span>
              <span className="tabular-nums font-bold text-[#0d0d0d]">{tally.assigned}</span>
              {tally.assigned === 1 ? ' person assigned' : ' people assigned'}
            </p>
          </div>
        </div>

        <div className="relative w-full sm:w-[200px]">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search roles"
            className="w-full h-8 bg-slate-50 border border-slate-200 rounded-md pl-7 pr-2.5 text-body text-[#0d0d0d] placeholder:text-slate-400 outline-none transition-colors focus:bg-white focus:border-[var(--theme-primary)] focus:ring-3 focus:ring-[var(--theme-primary-soft)]"
          />
        </div>

        <ViewToggle value={viewMode} onChange={setViewMode} />

        <button
          type="button"
          onClick={startNew}
          className="h-8 px-3 rounded-md text-white text-body font-semibold flex items-center gap-1.5 shadow-sm hover:opacity-90 transition-opacity shrink-0"
          style={{ background: 'var(--theme-primary)' }}
        >
          <Plus size={14} strokeWidth={2.4} aria-hidden="true" /> Add role
        </button>
      </header>

      {/* ── Banded by what wants attention, as cards or as a table ───────── */}
      {bands.map((band) => (
        <section key={band.key} className="flex flex-col gap-2">
          {viewMode === 'grid' ? (
            /* The card, the grid and the two footer buttons are the SuperAdmin
               Organizations list, reused rather than re-invented: bordered
               header, a two-column definition list of counted facts, and a
               footer of transparent icon-and-word buttons that grow a border on
               hover. Two consoles that manage the same shop should not each
               have their own idea of what a card is. */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 min-[1920px]:grid-cols-5 gap-4">
              {band.items.map((role) => {
                const m = measure(role);
                return (
                  <article
                    key={role.id}
                    className="bg-white border border-[var(--theme-tertiary)] rounded-lg p-4 flex flex-col gap-3 hover:border-[var(--theme-primary-tint)] transition-colors"
                  >
                    {/* The purpose sits where the Organizations card puts an
                        org's type: a descriptor under the name, not a labelled
                        field. A role name alone does not say whether a Cashier
                        may change prices, so the line earns its place — but it
                        is a subtitle, and labelling it "Purpose" spent a whole
                        row on a word the sentence already implies. */}
                    <header className="flex flex-col gap-1 border-b border-[var(--theme-tertiary)] pb-3">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-body font-extrabold uppercase tracking-wide text-[#0d0d0d] truncate">
                          {role.name}
                        </p>
                        {role.isLocked ? (
                          <span className="shrink-0 inline-flex items-center gap-1 text-micro font-bold uppercase tracking-[0.1em] text-amber-600">
                            <Lock size={10} aria-hidden="true" /> Built in
                          </span>
                        ) : m.staff === 0 ? (
                          <span className="shrink-0 text-micro font-bold uppercase tracking-[0.1em] text-slate-400">
                            Unused
                          </span>
                        ) : (
                          <span className="shrink-0 inline-flex items-center gap-1 text-micro font-bold uppercase tracking-[0.1em] text-emerald-600">
                            <Users size={10} aria-hidden="true" /> In use
                          </span>
                        )}
                      </div>
                      {/* Only when there is one. "No purpose set" spent a line
                          announcing an absence the reader can see for
                          themselves, on every card that happened to lack a
                          description — and the place to notice a missing
                          purpose is the editor, where it can be typed. */}
                      {role.description && (
                        <p className="text-micro font-medium text-slate-500 truncate" title={role.description}>
                          {role.description}
                        </p>
                      )}
                    </header>

                    {/* A built-in role already says its access twice — the band
                        it sits under is headed "Can do anything", and its own
                        badge reads BUILT IN. A third line saying "Everything"
                        is the same fact a third time, so the card gives that
                        space back and reports only what differs between two
                        built-in roles: who is on them. */}
                    <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-micro">
                      {!role.isLocked && (
                        <div>
                          <dt className="text-micro font-bold uppercase tracking-[0.16em] text-slate-400 mb-0.5">Permissions</dt>
                          <dd className="font-bold text-[var(--theme-primary)] truncate tabular-nums">
                            {m.granted} of {grantable || '—'}
                          </dd>
                        </div>
                      )}
                      <div>
                        <dt className="text-micro font-bold uppercase tracking-[0.16em] text-slate-400 mb-0.5">Staff</dt>
                        <dd className="font-semibold text-slate-700 truncate tabular-nums">
                          {m.staff === 0 ? 'None' : `${m.staff} ${m.staff === 1 ? 'person' : 'people'}`}
                        </dd>
                      </div>
                    </dl>

                    <footer className="flex items-center justify-end gap-2 mt-auto pt-3 border-t border-[var(--theme-tertiary)]">
                      <button
                        type="button"
                        onClick={() => startEdit(role)}
                        aria-label={`${role.isLocked ? 'View' : 'Edit'} ${role.name}`}
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-transparent border border-transparent text-slate-500 hover:bg-white hover:border-slate-300 hover:text-[var(--theme-primary)] hover:shadow-sm text-micro font-bold uppercase tracking-[0.1em] transition-all"
                      >
                        {role.isLocked
                          ? <><Eye size={12} aria-hidden="true" /> View</>
                          : <><Pen size={12} aria-hidden="true" /> Edit</>}
                      </button>
                      {/* Beside the thing it destroys, and only where it can
                          act: a role somebody still holds cannot go. The server
                          says so too — saying it here saves the trip and
                          explains why. */}
                      {!role.isLocked && (
                        <button
                          type="button"
                          onClick={() => setConfirming({ role })}
                          disabled={m.staff > 0}
                          aria-label={`Remove ${role.name}`}
                          title={m.staff > 0 ? 'Move these staff to another role first' : 'Delete this role'}
                          className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-transparent border border-transparent text-slate-500 hover:bg-white hover:border-slate-300 hover:text-rose-600 hover:shadow-sm text-micro font-bold uppercase tracking-[0.1em] transition-all disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:border-transparent disabled:hover:text-slate-500 disabled:hover:shadow-none"
                        >
                          <Trash2 size={12} aria-hidden="true" /> Remove
                        </button>
                      )}
                    </footer>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className={`${card} overflow-hidden`}>
              <div className="overflow-x-auto custom-scrollbar">
                <table className="w-full min-w-[720px] border-collapse">
                  <caption className="sr-only">{band.title} — {band.why}</caption>
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      {/* No purpose column. A sentence set in a narrow cell
                          truncates to the point of saying nothing, and the
                          table is here to compare figures — the purpose is on
                          the card and at the top of the role itself. */}
                      <th scope="col" className="text-left px-4 py-2 text-micro font-semibold text-slate-500">Role</th>
                      <th scope="col" className="text-right px-4 py-2 text-micro font-semibold text-slate-500 w-[110px]">Permissions</th>
                      <th scope="col" className="text-right px-4 py-2 text-micro font-semibold text-slate-500 w-[80px]">Areas</th>
                      <th scope="col" className="text-right px-4 py-2 text-micro font-semibold text-slate-500 w-[80px]">Staff</th>
                      <th scope="col" className="text-right px-4 py-2 text-micro font-semibold text-slate-500 w-[110px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {band.items.map((role) => {
                      const m = measure(role);
                      return (
                        <tr key={role.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/70 transition-colors">
                          <th scope="row" className="text-left font-normal px-4 py-2.5">
                            <span className="flex items-center gap-1.5 text-body font-semibold text-[#0d0d0d]">
                              {role.name}
                              {role.isLocked && <Lock size={11} className="text-amber-600 shrink-0" aria-label="Built in" />}
                            </span>
                          </th>
                          <td className="px-4 py-2.5 text-right text-body tabular-nums text-[#0d0d0d] font-medium">
                            {role.isLocked ? 'All' : (
                              <>{m.granted}<span className="text-slate-400"> / {grantable || '—'}</span></>
                            )}
                          </td>
                          <td
                            className="px-4 py-2.5 text-right text-body tabular-nums text-[#0d0d0d] font-medium"
                            title={m.areas.join(', ')}
                          >
                            {role.isLocked ? groups.length : m.areas.length}
                            <span className="text-slate-400"> / {groups.length}</span>
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            {m.staff === 0 ? (
                              <span className="text-body text-slate-400">None</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-body font-medium text-slate-600 tabular-nums">
                                <Users size={11} className="text-slate-400" aria-hidden="true" /> {m.staff}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2.5">
                            {/* Icons alone here, words on the card — which is
                                how the SuperAdmin Organizations list splits it
                                too. A table row is scanned, and a column of
                                repeated EDIT / REMOVE reads as noise once there
                                are eight of them; on a card there is one of each
                                and the word is worth its space. */}
                            <RowActions>
                              <IconAction
                                icon={role.isLocked ? Eye : Pen}
                                label={`${role.isLocked ? 'View' : 'Edit'} ${role.name}`}
                                title={role.isLocked ? 'View' : 'Edit'}
                                onClick={() => startEdit(role)}
                              />
                              {!role.isLocked && (
                                <IconAction
                                  icon={Trash2}
                                  tone="danger"
                                  label={`Remove ${role.name}`}
                                  title={m.staff > 0 ? 'Move these staff to another role first' : 'Remove'}
                                  disabled={m.staff > 0}
                                  onClick={() => setConfirming({ role })}
                                />
                              )}
                            </RowActions>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      ))}

      {shown.length === 0 && (
        <p className={`${card} text-center text-body text-slate-500 py-12`}>
          {search ? `No role matches “${search}”.` : 'No roles yet. Add one to decide what your staff can do.'}
        </p>
      )}

      {confirming && (
        <ConfirmModal
          open
          title={`Delete “${confirming.role?.name}”?`}
          message="Nobody is using this role, so nothing else changes."
          confirmText="Delete"
          onCancel={() => setConfirming(null)}
          onConfirm={doConfirm}
        />
      )}
    </div>
  );
};

RolesAclTab.propTypes = {
  org: PropTypes.object.isRequired,
  showNotification: PropTypes.func,
};
